import cn from 'classnames';
import { useEffect, useState } from 'react';

import {
    WELCOME_BANNER_BUTTONS_PER_PAGE,
    WelcomeBannerButton,
    WelcomeBannerImageMode,
    hasRenderableBadge,
    paginateBannerButtons,
} from 'src/utils/widgetSettings';

import './style.scss';
import { getTranslations } from '../../../../../../utils/i18n';

type Props = {
    enabled?: boolean;
    imageMode?: WelcomeBannerImageMode;
    imageUrl?: string | null;
    buttons?: WelcomeBannerButton[];
    /** Channel theme colour, hex WITHOUT a leading '#', as stored in the widget settings. */
    themeColor?: string;
    /** Sends the button's label as the visitor's message — a button carries no other action. */
    onSelect: (label: string) => void;
};

/**
 * IMBEE-8069 (T86) Area A — the welcome banner shown at the top of a new conversation.
 *
 * Layout matches the admin live preview in imbee-cms exactly: a fixed 76px image rail on the
 * left, and a column of 33px rows on the right, capped by a pager. The one intentional
 * difference is that here the pager works — in the admin preview it is inert, because paging
 * is widget behaviour and a clickable pager there would imply the operator is configuring
 * pages rather than one ordered list.
 *
 * Renders nothing unless switched on with something to show, so a channel without the
 * feature is unchanged.
 */
function WelcomeBanner({ enabled, imageMode, imageUrl, buttons, themeColor, onSelect }: Props) {
    const pages = paginateBannerButtons(buttons, WELCOME_BANNER_BUTTONS_PER_PAGE);
    const [pageIndex, setPageIndex] = useState(0);

    // Config can arrive after first render, and a shorter list must not strand the pager on a
    // page that no longer exists.
    useEffect(() => {
        if (pageIndex > pages.length - 1) setPageIndex(0);
    }, [pages.length, pageIndex]);

    const showImage = imageMode === WelcomeBannerImageMode.Client && Boolean(imageUrl);
    const hasButtons = pages.length > 0;

    if (!enabled || (!showImage && !hasButtons)) return null;

    const currentPage = pages[pageIndex] ?? [];
    const showPager = pages.length > 1;

    return (
        <div className="welcome-banner" data-testid="welcome-banner">
            {/*
              With no image this is simply a coloured panel — no placeholder artwork, since a
              generic icon reads as a broken image rather than as a deliberate blank. The colour
              follows the channel theme chosen earlier in the wizard, so the banner matches the
              rest of the widget instead of carrying a palette of its own.
            */}
            <div
                className="welcome-banner-rail"
                style={themeColor ? { backgroundColor: `#${themeColor}` } : undefined}
            >
                {/* Decorative: the rows beside it carry the meaning. Fills the rail edge to edge. */}
                {showImage && <img className="welcome-banner-image" src={imageUrl as string} alt="" />}
            </div>

            <div className="welcome-banner-body">
                {currentPage.map((button, index) => (
                    <button
                        type="button"
                        className="welcome-banner-row"
                        key={`${button.label}-${index}`}
                        onClick={() => onSelect(button.label)}
                    >
                        <span className="welcome-banner-title">{button.label}</span>
                        {hasRenderableBadge(button) && (
                            <span
                                className="welcome-banner-badge"
                                style={{ backgroundColor: `#${button.badge?.color}` }}
                            >
                                {button.badge?.text}
                            </span>
                        )}
                    </button>
                ))}

                {showPager && (
                    <div className="welcome-banner-footer">
                        <button
                            type="button"
                            className="welcome-banner-switch"
                            onClick={() => setPageIndex((current) => (current + 1) % pages.length)}
                        >
                            {getTranslations().welcomeBanner.switch}
                        </button>
                        <div className="welcome-banner-dots">
                            {pages.map((_, page) => (
                                <span
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`banner-page-${page}`}
                                    className={cn('welcome-banner-dot', { 'is-active': page === pageIndex })}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WelcomeBanner;
